import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CreateSurveyBody, CreateSurveyCont, StickyHeader } from './CreateSurvey.styles';
import CreateSurveyHeader from '../../components/CreateSurveyHeader/CreateSurveyHeader';
import DomainTabs from '../../components/DomainTabs/DomainTabs';
import Participants from '../../components/Participants/Participants';
import ReportsSetup from '../../components/ReportsSetup/ReportsSetup';
import ScheduleSurvey from '../../components/ScheduleSurvey/ScheduleSurvey';
import TabGroup from '../../components/TabGroup/TabGroup';
import { RouteNames } from '../../constans/routes';
import { CreateSurveyCardsNames, CreateSurveyTypeNames } from '../../constans/state';
import { useSurveyTable, useShareSurvey, useToast } from '../../hooks';
import useScheduleContent from '../../hooks/useScheduleContent';
import { useScroll } from '../../hooks/useScroll';
import { useSurveyContents } from '../../hooks/useSurveyContents';
import { useSurveyOverview } from '../../hooks/useSurveyOverview';
import { IItemReportResponse } from '../../services/reports';
import { IIntroOutro, ISurveyScope, putScheduleSurveyRequest } from '../../services/surveys';
import ShareResults from '../ShareResults/ShareResults';
import SurveyContents from '../SurveyContents/SurveyContents';

const defaultSurveyOverview = {
  id: '',
  name: '',
  type: '',
  baseLanguage: '',
  organizationName: '',
  externalSurveyId: '',
};

const CreateSurvey: FC = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const { getAppliedFilters, applyFilters } = useSurveyTable();
  const { getSurveyOverview, putSurveyContents } = useSurveyOverview();
  const { postShareResults } = useShareSurvey(params.surveyId || '1');
  const { isIntersecting } = useScroll('HelloScreenTitle', '28% 0% 33% 0%');
  const { showToastMessage } = useToast();
  const { t } = useTranslation();

  const [helloEndScreen, setHelloEndScreen] = React.useState<IIntroOutro>({
    introText: {},
    outroText: {},
    title: {},
    helpText: {},
  });
  const [surveyOverview, setSurveyOverview] = React.useState(defaultSurveyOverview);
  const [surveyScope, setSurveyScope] = useState<ISurveyScope[]>([]);
  const [pending, setPending] = useState(false);
  const [isTransitionBlocked, setIsTransitionBlocked] = React.useState(false);
  const [blockedTab, setBlockedTab] = React.useState(null);
  const [shareResultsItems, setShareResultsItems] = useState<IItemReportResponse[]>([]);

  const filters = getAppliedFilters();

  const { baseLanguage } = surveyOverview;

  const surveyId = params.surveyId || '1';
  const effectSurveyId =
    searchParams.get('domain') === CreateSurveyTypeNames.internal
      ? surveyId
      : surveyOverview.externalSurveyId;

  const defaultTab = filters?.card || CreateSurveyCardsNames.contents;
  const defaultDomain = filters?.domain;

  const effectType =
    defaultDomain === CreateSurveyTypeNames.external ? 'EFFECT_EXTERNAL' : surveyOverview.type;

  const tabs = [
    { name: t('viewSurvey.topBar.contents'), value: CreateSurveyCardsNames.contents },
    {
      name: t('viewSurvey.topBar.participantsandreports'),
      value: CreateSurveyCardsNames.participantsandreports,
    },
    {
      name: t('viewSurvey.topBar.scheduleSurvey'),
      value: CreateSurveyCardsNames.schedule,
    },
    { name: t('viewSurvey.topBar.shareResults'), value: CreateSurveyCardsNames.shareResults },
  ];

  const surveyContentsData = useSurveyContents(
    effectType,
    effectType === 'EFFECT_EXTERNAL' ? surveyOverview.externalSurveyId : surveyOverview.id,
    surveyOverview.organizationName
  );

  const { schedule, updateSchedule, updateAndSortReminders } = useScheduleContent(
    surveyId,
    setIsTransitionBlocked
  );

  const { linkToPreview, openQuestions } = surveyContentsData;

  const handleSetSurveyData = React.useCallback(
    (helloEndInit: IIntroOutro) => {
      setHelloEndScreen(helloEndInit);
    },
    [setHelloEndScreen]
  );

  const handleSetSurveyScope = React.useCallback(
    (scope: ISurveyScope[]) => {
      setSurveyScope(scope);
    },
    [setSurveyScope]
  );

  const onShareResults = useCallback(async () => {
    try {
      await postShareResults();

      // backend returns OK instantly, before actually sharing results
      // this adds additional 2 seconds for changes to take place
      setPending(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setPending(false);

      showToastMessage({
        description: t('viewSurvey.contents.notifications.share.description'),
        mode: 'success',
        title: t('viewSurvey.contents.notifications.share.title'),
      });
    } catch (error) {
      console.log({ error });
    }
  }, [surveyId, postShareResults]);

  const onConfirmQuit = () => {
    applyFilters({ ...filters, card: blockedTab });
    setBlockedTab(null);
  };

  const submitSurveyContents = useCallback(async () => {
    const { name } = surveyOverview ?? {};

    try {
      setPending(true);

      if (defaultTab === CreateSurveyCardsNames.schedule) {
        const updatedSchedule = await putScheduleSurveyRequest(surveyId, schedule);
        updateAndSortReminders(updatedSchedule);

        showToastMessage({
          description: t('viewSurvey.contents.notifications.schedule.description', { name }),
          mode: 'success',
          title: t('viewSurvey.contents.notifications.schedule.title'),
        });
      } else if (defaultTab === CreateSurveyCardsNames.contents) {
        await putSurveyContents(effectSurveyId ? effectSurveyId : surveyId, {
          scope: surveyScope,
          openQuestions: openQuestions ? openQuestions : [],
        });
        showToastMessage({
          description: t('viewSurvey.contents.notifications.save.description', { name }),
          mode: 'success',
          title: t('viewSurvey.contents.notifications.save.title'),
        });
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setPending(false);
      setIsTransitionBlocked(false);
    }
  }, [
    baseLanguage,
    helloEndScreen,
    effectSurveyId,
    surveyScope,
    surveyOverview,
    putSurveyContents,
    defaultTab,
    schedule,
  ]);

  useEffect(() => {
    try {
      if (surveyOverview === defaultSurveyOverview) {
        (async () => {
          const overview = await getSurveyOverview(surveyId);
          setSurveyOverview(overview);
        })();
      }
    } catch (error) {
      navigate(RouteNames.Page404);
    }
  }, [surveyId, getSurveyOverview]);

  const renderContent = () => {
    switch (defaultTab) {
      case CreateSurveyCardsNames.contents: {
        if (!(surveyOverview.type && surveyOverview.id)) {
          return null;
        }

        const overview = {
          ...surveyOverview,
          type:
            defaultDomain === CreateSurveyTypeNames.external
              ? 'EFFECT_EXTERNAL'
              : surveyOverview.type,
        };

        return (
          <SurveyContents
            surveyOverview={overview}
            isIntersecting={isIntersecting}
            isTransitionBlocked={isTransitionBlocked}
            onSetSurveyData={handleSetSurveyData}
            onSetSurveyScope={handleSetSurveyScope}
            setIsTransitionBlocked={setIsTransitionBlocked}
            surveyContentsData={surveyContentsData}
            onConfirmQuit={onConfirmQuit}
          />
        );
      }
      case CreateSurveyCardsNames.participantsandreports: {
        if (defaultDomain === CreateSurveyTypeNames.participants) {
          return (
            <Participants
              baseLang={surveyOverview?.baseLanguage}
              surveyType={surveyOverview.type}
            />
          );
        }

        return defaultDomain === CreateSurveyTypeNames.reportsSetup &&
          surveyOverview.type &&
          surveyOverview.id ? (
            <ReportsSetup surveyOverview={surveyOverview} />
          ) : null;
      }
      case CreateSurveyCardsNames.shareResults: {
        return (
          <ShareResults
            shareResultsItems={shareResultsItems}
            setShareResultsItems={setShareResultsItems}
            id={surveyOverview.id}
          />
        );
      }
      case CreateSurveyCardsNames.schedule:
        return (
          <ScheduleSurvey
            schedule={schedule}
            externalSurveyId={surveyOverview.externalSurveyId}
            updateSchedule={updateSchedule}
            isTransitionBlocked={isTransitionBlocked}
            setIsTransitionBlocked={setIsTransitionBlocked}
            onConfirmQuit={onConfirmQuit}
          />
        );
      case CreateSurveyCardsNames.results:
        return <div>results</div>;
    }
  };

  return (
    <CreateSurveyCont>
      <>
        <StickyHeader
          id="StickyHeader"
          isIntersecting={isIntersecting}
          isSticky={defaultTab === CreateSurveyCardsNames.contents}
        >
          <CreateSurveyHeader
            surveyName={surveyOverview.name}
            organizationName={surveyOverview.organizationName}
            scroll={!isIntersecting && defaultTab === CreateSurveyCardsNames.contents}
            loading={pending}
            onShareResults={onShareResults}
            onSubmitContents={submitSurveyContents}
            preview_link={linkToPreview}
            isDisabled={shareResultsItems.length === 0}
          />
          <TabGroup
            tabs={tabs}
            surveyOverviewType={surveyOverview.type}
            applyFilters={applyFilters}
            defaultValue={defaultTab}
            selectedTab={filters?.card}
            setBlockedTab={setBlockedTab}
            isTransitionBlocked={isTransitionBlocked}
          />
        </StickyHeader>
      </>
      <DomainTabs
        surveyOverviewType={surveyOverview.type}
        defaultTab={defaultTab}
        defaultDomain={defaultDomain}
        applyFilters={applyFilters}
      >
        <CreateSurveyBody data-testid={`${defaultTab}Body`}>{renderContent()}</CreateSurveyBody>
      </DomainTabs>
    </CreateSurveyCont>
  );
};

export default CreateSurvey;
